/**
 * Alshallal Pastry Company HR System - My Requests Page
 * Phase 5: Generalized Requests & Approvals
 * Phase 24.2: Enhanced with Request Timeline view
 * Phase 25.1: Enhanced with Document Preview component
 * Employee view for submitting and viewing their requests
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { canCancelRequest as canCancelByDate } from '@/lib/requestLifecycle';
import { format } from 'date-fns';
import {
  ClipboardList,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Paperclip,
  Upload,
  X,
  Loader2,
  FileText,
  Eye,
  Download
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import RequestTimeline from '@/components/requests/RequestTimeline';
import DocumentPreview from '@/components/documents/DocumentPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_STYLES = {
  pending_manager: { bg: 'bg-amber-100 text-amber-800', icon: Clock, label: { en: 'Pending Manager', ar: 'بانتظار المدير' } },
  pending_hr: { bg: 'bg-blue-100 text-blue-800', icon: Clock, label: { en: 'Pending HR', ar: 'بانتظار الموارد البشرية' } },
  approved: { bg: 'bg-green-100 text-green-800', icon: CheckCircle, label: { en: 'Approved', ar: 'موافق عليه' } },
  rejected: { bg: 'bg-red-100 text-red-800', icon: XCircle, label: { en: 'Rejected', ar: 'مرفوض' } },
  cancelled: { bg: 'bg-gray-100 text-gray-800', icon: XCircle, label: { en: 'Cancelled', ar: 'ملغى' } },
};

const MyRequestsPage = () => {
  const { token } = useAuth();
  const { isRTL, language } = useLanguage();

  // State
  const [requests, setRequests] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Create dialog
  const [createDialog, setCreateDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({});
  const [formNotes, setFormNotes] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [formError, setFormError] = useState(null);

  // View dialog
  const [viewDialog, setViewDialog] = useState({ open: false, request: null });

  // Document preview dialog (Phase 25.1)
  const [previewDialog, setPreviewDialog] = useState({ open: false, attachment: null });

  // Cancel dialog
  const [cancelDialog, setCancelDialog] = useState({ open: false, request: null });
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Fetch request types
  const fetchRequestTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/requests/types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequestTypes(response.data.request_types || []);
    } catch (err) {
      console.error('Failed to fetch request types:', err);
    }
  }, [token]);

  // Fetch requests
  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/requests/my?page=${page}&page_size=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.requests || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (err) {
      if (err.response?.status !== 400) {
        setError(err.response?.data?.detail?.error || 'Failed to load requests');
      }
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => {
    fetchRequestTypes();
    fetchRequests();
  }, [fetchRequestTypes, fetchRequests]);

  // Handle type selection
  const handleTypeSelect = (typeId) => {
    const type = requestTypes.find(t => t.id === typeId);
    setSelectedType(type);
    setFormData({});
    setFormError(null);
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);

      const response = await axios.post(`${API_URL}/requests/attachments/upload`, formDataObj, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setAttachments(prev => [...prev, response.data]);
    } catch (err) {
      setFormError(err.response?.data?.detail?.error || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  // Remove attachment
  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  // Validate form
  const validateForm = () => {
    if (!selectedType) return false;
    
    const schema = selectedType.required_fields_schema;
    if (!schema || !schema.fields) return true;

    for (const field of schema.fields) {
      const value = formData[field.name];
      if (field.required && (!value || value === '')) {
        setFormError(`${field.label} is required`);
        return false;
      }
      if (value && field.min_length && value.length < field.min_length) {
        setFormError(`${field.label} must be at least ${field.min_length} characters`);
        return false;
      }
    }

    // Check attachment requirement
    if (selectedType.requires_attachment && attachments.length === 0) {
      setFormError('Attachment is required for this request type');
      return false;
    }

    return true;
  };

  // Submit request
  const handleSubmit = async () => {
    setFormError(null);
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      
      await axios.post(`${API_URL}/requests/`, {
        request_type_id: selectedType.id,
        form_data: formData,
        notes: formNotes || undefined,
        attachment_ids: attachments.map(a => a.id)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCreateDialog(false);
      setSelectedType(null);
      setFormData({});
      setFormNotes('');
      setAttachments([]);
      fetchRequests();
    } catch (err) {
      setFormError(err.response?.data?.detail?.error || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle cancel request
  const handleCancelRequest = async () => {
    if (!cancelDialog.request) return;
    
    try {
      setCancelling(true);
      const params = cancelReason ? `?reason=${encodeURIComponent(cancelReason)}` : '';
      await axios.post(
        `${API_URL}/requests/${cancelDialog.request.id}/cancel${params}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCancelDialog({ open: false, request: null });
      setCancelReason('');
      fetchRequests();
    } catch (err) {
      setError(err.response?.data?.detail?.error || 'Failed to cancel request');
    } finally {
      setCancelling(false);
    }
  };

  // Check if request can be cancelled
  const canCancelRequest = (request) => {
    return canCancelByDate(request);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'PP');
    } catch {
      return dateStr;
    }
  };

  const getStatusStyle = (status) => STATUS_STYLES[status] || STATUS_STYLES.pending_manager;
  const getStatusLabel = (status) => {
    const style = getStatusStyle(status);
    return language === 'ar' ? style.label.ar : style.label.en;
  };

  // Render dynamic form field
  const renderFormField = (field) => {
    const value = formData[field.name] || '';
    const label = language === 'ar' && field.label_ar ? field.label_ar : field.label;
    const placeholder = language === 'ar' && field.placeholder_ar ? field.placeholder_ar : field.placeholder;

    switch (field.type) {
      case 'select':
        return (
          <div key={field.name} className="space-y-2">
            <Label>{label} {field.required && '*'}</Label>
            <Select
              value={value}
              onValueChange={(v) => setFormData(prev => ({ ...prev, [field.name]: v }))}
            >
              <SelectTrigger data-testid={`field-${field.name}`}>
                <SelectValue placeholder={isRTL ? 'اختر...' : 'Select...'} />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map(opt => (
              <SelectItem key={opt.value} value={opt.value} data-testid={`field-option-${field.name}-${opt.value}`}>
                    {language === 'ar' && opt.label_ar ? opt.label_ar : opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'textarea':
        return (
          <div key={field.name} className="space-y-2">
            <Label>{label} {field.required && '*'}</Label>
            <Textarea
              value={value}
              onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
              placeholder={placeholder}
              rows={3}
              data-testid={`field-${field.name}`}
            />
            {field.min_length && value.length > 0 && value.length < field.min_length && (
              <p className="text-sm text-amber-600">
                {isRTL ? `${field.min_length - value.length} حرف متبقي` : `${field.min_length - value.length} more characters needed`}
              </p>
            )}
          </div>
        );

      case 'date':
        return (
          <div key={field.name} className="space-y-2">
            <Label>{label} {field.required && '*'}</Label>
            <Input
              type="date"
              min={field.min_date || undefined}
              value={value}
              onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
              data-testid={`field-${field.name}`}
            />
          </div>
        );

      case 'number':
        return (
          <div key={field.name} className="space-y-2">
            <Label>{label} {field.required && '*'}</Label>
            <Input
              type="number"
              value={value}
              onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
              placeholder={placeholder}
              min={field.min_value}
              max={field.max_value}
              data-testid={`field-${field.name}`}
            />
          </div>
        );

      default: // text
        return (
          <div key={field.name} className="space-y-2">
            <Label>{label} {field.required && '*'}</Label>
            <Input
              type="text"
              value={value}
              onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
              placeholder={placeholder}
              data-testid={`field-${field.name}`}
            />
            {field.min_length && value.length > 0 && value.length < field.min_length && (
              <p className="text-sm text-amber-600">
                {isRTL ? `${field.min_length - value.length} حرف متبقي` : `${field.min_length - value.length} more characters needed`}
              </p>
            )}
          </div>
        );
    }
  };

  return (
    <div className="space-y-6" data-testid="my-requests-page">
      {/* Header - responsive */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
            {isRTL ? 'طلباتي' : 'My Requests'}
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">
            {isRTL ? 'تقديم وعرض طلباتك' : 'Submit and view your requests'}
          </p>
        </div>
        <Button onClick={() => setCreateDialog(true)} data-testid="new-request-btn" className="w-full sm:w-auto">
          <Plus className="h-4 w-4 me-2" />
          {isRTL ? 'طلب جديد' : 'New Request'}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ms-auto text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {/* Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle>{isRTL ? 'الطلبات' : 'Requests'}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : requests.length === 0 ? (
            <div className="py-12 text-center">
              <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {isRTL ? 'لا توجد طلبات' : 'No Requests'}
              </h3>
              <p className="text-slate-600 mb-4">
                {isRTL ? 'لم تقدم أي طلبات بعد' : "You haven't submitted any requests yet"}
              </p>
              <Button onClick={() => setCreateDialog(true)}>
                <Plus className="h-4 w-4 me-2" />
                {isRTL ? 'طلب جديد' : 'New Request'}
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table className="min-w-[600px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{isRTL ? 'نوع الطلب' : 'Request Type'}</TableHead>
                      <TableHead className="hidden sm:table-cell">{isRTL ? 'تاريخ التقديم' : 'Submitted'}</TableHead>
                      <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                      <TableHead>{isRTL ? 'المرفقات' : 'Attachments'}</TableHead>
                      <TableHead className="text-end">{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((request) => {
                      const statusStyle = getStatusStyle(request.status);
                      const StatusIcon = statusStyle.icon;
                      return (
                        <TableRow key={request.id} data-testid={`request-row-${request.id}`}>
                          <TableCell className="font-medium">
                            {language === 'ar' && request.request_type_name_ar ? request.request_type_name_ar : request.request_type_name}
                          </TableCell>
                          <TableCell className="text-slate-500 hidden sm:table-cell">{formatDate(request.submitted_at)}</TableCell>
                          <TableCell>
                            <Badge className={statusStyle.bg}>
                              <StatusIcon className="h-3 w-3 me-1" />
                              <span className="hidden xs:inline">{getStatusLabel(request.status)}</span>
                            </Badge>
                            {request.rejection_reason && (
                              <p className="text-xs text-red-600 mt-1 max-w-[120px] truncate">{request.rejection_reason}</p>
                            )}
                          </TableCell>
                          <TableCell>
                            {request.attachment_ids?.length > 0 && (
                              <Badge variant="outline">
                                <Paperclip className="h-3 w-3 me-1" />
                                {request.attachment_ids.length}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-end">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setViewDialog({ open: true, request })}
                                data-testid={`view-btn-${request.id}`}
                              >
                                <Eye className="h-4 w-4" />
                                <span className="hidden sm:inline ms-1">{isRTL ? 'عرض' : 'View'}</span>
                              </Button>
                              {canCancelRequest(request) && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => setCancelDialog({ open: true, request })}
                                  data-testid={`cancel-btn-${request.id}`}
                                >
                                  <XCircle className="h-4 w-4" />
                                  <span className="hidden sm:inline ms-1">{isRTL ? 'إلغاء' : 'Cancel'}</span>
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-slate-600">
                    {isRTL ? `عرض ${requests.length} من ${total}` : `Showing ${requests.length} of ${total}`}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                      {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                    </Button>
                    <span className="text-sm">{page} / {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                      {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Request Dialog */}
      <Dialog open={createDialog} onOpenChange={(open) => {
        if (!open) {
          setCreateDialog(false);
          setSelectedType(null);
          setFormData({});
          setFormNotes('');
          setAttachments([]);
          setFormError(null);
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'طلب جديد' : 'New Request'}</DialogTitle>
            <DialogDescription>
              {isRTL ? 'حدد نوع الطلب واملأ التفاصيل' : 'Select request type and fill in the details'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {formError}
              </div>
            )}

            {/* Request Type Selection */}
            <div className="space-y-2">
              <Label>{isRTL ? 'نوع الطلب' : 'Request Type'} *</Label>
              <Select
                value={selectedType?.id || ''}
                onValueChange={handleTypeSelect}
              >
                <SelectTrigger data-testid="request-type-select">
                  <SelectValue placeholder={isRTL ? 'اختر نوع الطلب' : 'Select request type'} />
                </SelectTrigger>
                <SelectContent>
                  {requestTypes.map(type => (
                  <SelectItem key={type.id} value={type.id} data-testid={`request-type-option-${type.id}`}>
                      {language === 'ar' && type.name_ar ? type.name_ar : type.name}
                      {type.requires_attachment && ' 📎'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedType && (
                <p className="text-sm text-slate-500">
                  {language === 'ar' && selectedType.description_ar ? selectedType.description_ar : selectedType.description}
                </p>
              )}
            </div>

            {/* Dynamic Form Fields */}
            {selectedType && selectedType.required_fields_schema?.fields?.map(renderFormField)}

            {/* Notes */}
            {selectedType && (
              <div className="space-y-2">
                <Label>{isRTL ? 'ملاحظات إضافية' : 'Additional Notes'}</Label>
                <Textarea
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  placeholder={isRTL ? 'أي ملاحظات إضافية...' : 'Any additional notes...'}
                  rows={2}
                  data-testid="notes-input"
                />
              </div>
            )}

            {/* Attachments */}
            {selectedType && (
              <div className="space-y-2">
                <Label>
                  {isRTL ? 'المرفقات' : 'Attachments'}
                  {selectedType.requires_attachment && ' *'}
                </Label>
                
                {/* Uploaded files */}
                {attachments.length > 0 && (
                  <div className="space-y-2">
                    {attachments.map(att => (
                      <div key={att.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded border">
                        <FileText className="h-4 w-4 text-slate-500" />
                        <span className="flex-1 text-sm truncate">{att.file_name}</span>
                        <span className="text-xs text-slate-400">{(att.file_size / 1024).toFixed(1)} KB</span>
                        <button
                          onClick={() => removeAttachment(att.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload button */}
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    id="attachment-upload"
                    className="hidden"
                    onChange={handleFileUpload}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  />
                  <label htmlFor="attachment-upload">
                    <Button type="button" variant="outline" size="sm" asChild disabled={uploadingFile}>
                      <span className="cursor-pointer">
                        {uploadingFile ? (
                          <><Loader2 className="h-4 w-4 me-2 animate-spin" />{isRTL ? 'جاري الرفع...' : 'Uploading...'}</>
                        ) : (
                          <><Upload className="h-4 w-4 me-2" />{isRTL ? 'رفع ملف' : 'Upload File'}</>
                        )}
                      </span>
                    </Button>
                  </label>
                  <span className="text-xs text-slate-400">
                    PDF, JPG, PNG, DOC, XLS (max 10MB)
                  </span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !selectedType}
              data-testid="submit-request-btn"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 me-2 animate-spin" />{isRTL ? 'جاري الإرسال...' : 'Submitting...'}</>
              ) : (
                isRTL ? 'إرسال الطلب' : 'Submit Request'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Request Dialog */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => !open && setViewDialog({ open: false, request: null })}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {viewDialog.request && (language === 'ar' && viewDialog.request.request_type_name_ar ? viewDialog.request.request_type_name_ar : viewDialog.request?.request_type_name)}
            </DialogTitle>
            <DialogDescription>
              {isRTL ? 'تفاصيل الطلب ومسار الموافقة' : 'Request Details & Approval Journey'}
            </DialogDescription>
          </DialogHeader>

          {viewDialog.request && (
            <Tabs defaultValue="timeline" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="timeline" data-testid="tab-timeline">
                  {isRTL ? 'مسار الطلب' : 'Timeline'}
                </TabsTrigger>
                <TabsTrigger value="details" data-testid="tab-details">
                  {isRTL ? 'التفاصيل' : 'Details'}
                </TabsTrigger>
              </TabsList>
              
              {/* Timeline Tab */}
              <TabsContent value="timeline" className="mt-4">
                <RequestTimeline
                  request={viewDialog.request}
                  type="request"
                  language={language}
                  isRTL={isRTL}
                />
              </TabsContent>
              
              {/* Details Tab */}
              <TabsContent value="details" className="mt-4 space-y-4">
                {/* Status */}
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">{isRTL ? 'الحالة:' : 'Status:'}</span>
                  <Badge className={getStatusStyle(viewDialog.request.status).bg}>
                    {getStatusLabel(viewDialog.request.status)}
                  </Badge>
                </div>

                {/* Submitted */}
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">{isRTL ? 'تاريخ التقديم:' : 'Submitted:'}</span>
                  <span>{formatDate(viewDialog.request.submitted_at)}</span>
                </div>

                {/* Form Data */}
                {viewDialog.request.form_data && Object.keys(viewDialog.request.form_data).length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium">{isRTL ? 'التفاصيل:' : 'Details:'}</h4>
                    <div className="bg-slate-50 p-3 rounded space-y-1">
                      {Object.entries(viewDialog.request.form_data).map(([key, value]) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-slate-600 capitalize">{key.replace(/_/g, ' ')}:</span>
                          <span className="font-medium">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {viewDialog.request.notes && (
                  <div className="space-y-2">
                    <h4 className="font-medium">{isRTL ? 'ملاحظات:' : 'Notes:'}</h4>
                    <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded">{viewDialog.request.notes}</p>
                  </div>
                )}

                {/* Rejection Reason */}
                {viewDialog.request.rejection_reason && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-red-600">{isRTL ? 'سبب الرفض:' : 'Rejection Reason:'}</h4>
                    <p className="text-sm text-red-600 bg-red-50 p-3 rounded">{viewDialog.request.rejection_reason}</p>
                  </div>
                )}
                
                {/* Charged to Annual Leave Indicator */}
                {viewDialog.request.deduct_from_annual && (
                  <div className="space-y-2 p-3 bg-amber-50 border border-amber-200 rounded">
                    <h4 className="font-medium text-amber-800 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      {isRTL ? 'مخصوم من الإجازة السنوية' : 'Charged to Annual Leave'}
                    </h4>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-amber-700">{isRTL ? 'الأيام المخصومة:' : 'Deducted Days:'}</span>
                        <span className="font-medium text-amber-800">{viewDialog.request.annual_deduct_days}</span>
                      </div>
                      {viewDialog.request.annual_deduct_reason && (
                        <div>
                          <span className="text-amber-700">{isRTL ? 'السبب:' : 'Reason:'}</span>
                          <p className="text-amber-800 mt-1">{viewDialog.request.annual_deduct_reason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Attachments */}
                {viewDialog.request.attachments?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium">{isRTL ? 'المرفقات:' : 'Attachments:'}</h4>
                    {viewDialog.request.attachments.map(att => (
                      <div key={att.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded border">
                        <FileText className="h-4 w-4 text-slate-500" />
                        <span className="flex-1 text-sm">{att.file_name}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPreviewDialog({ open: true, attachment: att })}
                          title={isRTL ? 'معاينة' : 'Preview'}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <a
                          href={`${API_URL}/requests/attachments/${att.id}/download`}
                          className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Download className="h-3 w-3" />
                          {isRTL ? 'تحميل' : 'Download'}
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialog({ open: false, request: null })}>
              {isRTL ? 'إغلاق' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Request Dialog */}
      <Dialog open={cancelDialog.open} onOpenChange={(open) => {
        if (!open) {
          setCancelDialog({ open: false, request: null });
          setCancelReason('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {isRTL ? 'إلغاء الطلب' : 'Cancel Request'}
            </DialogTitle>
            <DialogDescription>
              {isRTL 
                ? 'هل أنت متأكد من رغبتك في إلغاء هذا الطلب؟ لا يمكن التراجع عن هذا الإجراء.'
                : 'Are you sure you want to cancel this request? This action cannot be undone.'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {cancelDialog.request && (
              <div className="p-3 bg-slate-50 rounded">
                <p className="font-medium">
                  {language === 'ar' && cancelDialog.request.request_type_name_ar 
                    ? cancelDialog.request.request_type_name_ar 
                    : cancelDialog.request.request_type_name}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {isRTL ? 'تاريخ التقديم:' : 'Submitted:'} {formatDate(cancelDialog.request.submitted_at)}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>{isRTL ? 'سبب الإلغاء (اختياري)' : 'Cancellation Reason (optional)'}</Label>
              <Textarea
                value={cancelReason}
                data-testid="cancel-request-reason"
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder={isRTL ? 'اذكر سبب إلغاء الطلب...' : 'Provide a reason for cancellation...'}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setCancelDialog({ open: false, request: null })}
              disabled={cancelling}
              data-testid="cancel-request-dismiss"
            >
              {isRTL ? 'تراجع' : 'Go Back'}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancelRequest}
              disabled={cancelling}
              data-testid="cancel-request-confirm"
            >
              {cancelling ? (
                <Loader2 className="h-4 w-4 me-2 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4 me-2" />
              )}
              {isRTL ? 'إلغاء الطلب' : 'Cancel Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 25.1: Document Preview for Attachments */}
      <DocumentPreview
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, attachment: null })}
        document={previewDialog.attachment ? {
          id: previewDialog.attachment.id,
          file_name: previewDialog.attachment.file_name,
          mime_type: previewDialog.attachment.mime_type
        } : null}
        downloadUrl={previewDialog.attachment ? `${API_URL}/requests/attachments/${previewDialog.attachment.id}/download` : null}
        token={token}
        isRTL={isRTL}
        documentType="employee"
      />
    </div>
  );
};

export default MyRequestsPage;
